import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchAPI } from '@/api';
import { updateSubscriptionDataFromHook } from '@/actions';

const useSubscriptionData = (auth) => {
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState({
                subscription: null,
                currentPlan: null,
                plans: null,
                planPeriod: null,
                customerPortalData: [],
                invoiceUrl: null,
                invoices: null,
                projectsCount: 0,
                salesVolume: 0,
                accessCodesUsed: 0,
                discounts: [],
	});

	const fetchDataRequest = async () => {
		try {
			const response = await fetchAPI("/billing/status", auth.user.token, { showNewPlans: true });
			if (!response.ok) {
				throw new Error(`HTTP error: ${response.status}`);
			}

			const responseData = await response.json();

			if (responseData) {
                                const newData = {
                                        subscription: responseData.subscription || null,
                                        currentPlan: responseData.plan || null,
                                        plans: responseData.plans || null,
                                        planPeriod: responseData.plan?.period || 'year',
                                        customerPortalData: responseData.buttons || [],
                                        invoiceUrl: responseData.invoice || null,
                                        invoices: responseData.invoices || null,
                                        projectsCount: responseData.projectsCount || 0,
                                        salesVolume: responseData.salesVolume || 0,
                                        accessCodesUsed: responseData.accessCodesUsed || 0,
                                        salesLimit: responseData.plan?.salesLimit || null,
                                        discounts: responseData.discounts || [],
                                };

				setData(newData);
				
				const notificationData = {
					subscription: responseData.subscription || null,
					invoices: responseData.invoices || null,
					salesVolume: responseData.salesVolume || 0,
					salesLimit: responseData.plan?.salesLimit || null
				};
				
				dispatch(updateSubscriptionDataFromHook(notificationData));
				
				return responseData;
			}
			return null;
		} catch (error) {
			console.error('Error fetching subscription data:', error);
			return null;
		}
	};

	const checkSuccess = async () => {
		const responseData = await fetchDataRequest();
		return (responseData && responseData.subscription && Object.keys(responseData.subscription).length > 0);
	};

	const generateIntervals = (initialInterval, factor, maxInterval, steps) => {
		const intervals = [];
		let currentInterval = initialInterval;

		for (let i = 0; i < steps; i++) {
			intervals.push(currentInterval);
			currentInterval = Math.min(currentInterval * factor, maxInterval);
		}

		return intervals;
	};

	useEffect(() => {
		if (typeof searchParams.get('subscribed') === 'string') {
			const intervals = generateIntervals(3000, 1.5, 30000, 10); // starts at 3s, 1.5x each time, max 30s

			let timeoutId;

			const retryCheckSuccess = async (intervalIndex = 0) => {
				const success = await checkSuccess();

				if (!success && intervalIndex < intervals.length) {
					const nextInterval = intervals[intervalIndex];
					timeoutId = setTimeout(() => retryCheckSuccess(intervalIndex + 1), nextInterval);
				}
			};

			retryCheckSuccess();

			return () => {
				if (timeoutId) {
					clearTimeout(timeoutId);
				}
			};
		}
	}, [searchParams]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				await fetchDataRequest();
			} catch (error) {
				console.error('Error in fetchData:', error);
			} finally {
				setIsLoading(false);
			}
		};

		if (auth && auth.user && auth.user.token) {
			fetchData();
		} else {
			setIsLoading(false);
		}

	}, [auth]);

	return {
		isLoading,
		setIsLoading,
		...data,
		setPlanPeriod: (period) => setData(prev => ({ ...prev, planPeriod: period })),
		setSubscription: (sub) => {
			setData(prev => ({ ...prev, subscription: sub }));
			dispatch(updateSubscriptionDataFromHook({
				...data,
				subscription: sub
			}));
		},
		setCurrentPlan: (plan) => {
			setData(prev => ({ ...prev, currentPlan: plan }));
			dispatch(updateSubscriptionDataFromHook({
				...data,
				salesLimit: plan?.salesLimit || null
			}));
		}
	};
};

export default useSubscriptionData;
